import { MinusSmIcon, PlusSmIcon } from '@heroicons/react/outline';
import React, { useEffect, useRef, useState } from 'react';

const COLOR_GROUPS = [
  [
    { value: '#bbf7d0', className: 'bg-green-200' },
    { value: '#4ade80', className: 'bg-green-400' },
    { value: '#22c55e', className: 'bg-green-500' },
    { value: '#16a34a', className: 'bg-green-600' },
  ],
  [
    { value: '#fed7aa', className: 'bg-orange-200' },
    { value: '#fb923c', className: 'bg-orange-400' },
    { value: '#f97316', className: 'bg-orange-500' },
    { value: '#ea580c', className: 'bg-orange-600' },
  ],
  [
    { value: '#fef08a', className: 'bg-yellow-200' },
    { value: '#facc15', className: 'bg-yellow-400' },
    { value: '#eab308', className: 'bg-yellow-500' },
    { value: '#ca8a04', className: 'bg-yellow-600' },
  ],
  [
    { value: '#fecaca', className: 'bg-red-200' },
    { value: '#f87171', className: 'bg-red-400' },
    { value: '#ef4444', className: 'bg-red-500' },
    { value: '#dc2626', className: 'bg-red-600' },
  ],
  [
    { value: '#e5e5e5', className: 'bg-neutral-200' },
    { value: '#a3a3a3', className: 'bg-neutral-400' },
    { value: '#bfdbfe', className: 'bg-blue-200' },
    { value: '#60a5fa', className: 'bg-blue-400' },
  ],
];

const COLOR_PALETTE_MIN_HEIGHT = 260;

function getAnswerOptionValue(answerPossibility) {
  if (typeof answerPossibility === 'string') return answerPossibility;
  return answerPossibility?.opt || '';
}

function shouldOpenPaletteUpwards(element) {
  if (!element) return false;

  const rect = element.getBoundingClientRect();
  const spaceBelow = window.innerHeight - rect.bottom;
  const spaceAbove = rect.top;

  return spaceBelow < COLOR_PALETTE_MIN_HEIGHT && spaceAbove > spaceBelow;
}

function AnswerOptionColorPicker({
  answerLabel,
  selectedColor,
  open,
  placement,
  buttonRef,
  menuRef,
  onToggle,
  onSelect,
  onClear,
}) {
  return (
    <div className="relative">
      <button
        ref={buttonRef}
        type="button"
        aria-expanded={open}
        aria-haspopup="dialog"
        aria-label={`Choose color for ${answerLabel}`}
        className="relative mx-1 w-10 h-10 shrink-0 rounded-lg bg-neutral-200 text-neutral-600"
        style={selectedColor ? { backgroundColor: selectedColor } : undefined}
        onClick={onToggle}
      >
        {!selectedColor && (
          <span className="absolute inset-0 flex items-center justify-center">+</span>
        )}
      </button>

      {open && (
        <div
          ref={menuRef}
          role="dialog"
          aria-label={`Colors for ${answerLabel}`}
          data-placement={placement}
          className={
            'absolute z-20 right-0 rounded-lg shadow-basic bg-white ' +
            (placement === 'top' ? 'bottom-full mb-2' : 'top-full mt-2')
          }
        >
          <p className="text-sm pt-2">Colors</p>
          <div className="flex pb-2.5 px-4">
            {COLOR_GROUPS.map((group, groupIndex) => (
              <div key={groupIndex} className={groupIndex === 0 ? '' : 'ml-3.5'}>
                {group.map((color) => (
                  <button
                    key={color.value}
                    type="button"
                    aria-label={`Select color ${color.value} for ${answerLabel}`}
                    onClick={() => onSelect(color.value)}
                    className={`color-btn ${color.className}`}
                  ></button>
                ))}
              </div>
            ))}
          </div>
          <div className="px-2.5 pt-0.5 pb-2">
            <button
              type="button"
              aria-label={`Clear color for ${answerLabel}`}
              onClick={onClear}
              className="rounded-full hover:scale-[1.025] transition-all w-[100%] bg-neutral-500 text-sm py-1 text-white"
            >
              Clear
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

const AdditionalPropsMenu = React.memo(
  ({ field, i, j, categoryType, handleClickOutside, setFormData }) => {
    const menuRef = useRef();
    const colorMenuRef = useRef();
    const colorButtonRefs = useRef({});
    const [openColorIndex, setOpenColorIndex] = useState(null);
    const [colorPlacement, setColorPlacement] = useState('bottom');

    useEffect(() => {
      const onMouseDown = (e) => handleClickOutside(e, menuRef);
      document.addEventListener('mousedown', onMouseDown);

      return () => {
        document.removeEventListener('mousedown', onMouseDown);
      };
    }, [handleClickOutside]);

    useEffect(() => {
      if (openColorIndex === null) return;

      const updatePlacement = () => {
        const currentButton = colorButtonRefs.current[openColorIndex];
        setColorPlacement(shouldOpenPaletteUpwards(currentButton) ? 'top' : 'bottom');
      };

      const closeOnOutsideClick = (event) => {
        const currentButton = colorButtonRefs.current[openColorIndex];

        if (colorMenuRef.current?.contains(event.target) || currentButton?.contains(event.target)) {
          return;
        }

        setOpenColorIndex(null);
      };

      updatePlacement();
      document.addEventListener('mousedown', closeOnOutsideClick);
      window.addEventListener('resize', updatePlacement);
      window.addEventListener('scroll', updatePlacement, true);

      return () => {
        document.removeEventListener('mousedown', closeOnOutsideClick);
        window.removeEventListener('resize', updatePlacement);
        window.removeEventListener('scroll', updatePlacement, true);
      };
    }, [openColorIndex]);

    function handlePlaceholderChange(e, categoryIndex, rowNumber) {
      setFormData((prev) => {
        const updatedFormData = { ...prev };

        updatedFormData.categories[categoryIndex].fields[rowNumber].placeholder = e.target.value;

        return updatedFormData;
      });
    }

    function handleValueTypeSelect(value, categoryIndex, rowNumber) {
      setFormData((prev) => {
        const updatedFormData = { ...prev };
        const target = updatedFormData.categories[categoryIndex].fields[rowNumber];
        if (value === 'auto') {
          delete target.valueType;
        } else {
          target.valueType = value;
        }
        return updatedFormData;
      });
    }

    function addAnswerOpt(categoryIndex, rowNumber) {
      setFormData((prev) => {
        const updatedFormData = { ...prev };

        if (!updatedFormData.categories[categoryIndex].fields[rowNumber].answer_possibilities) {
          updatedFormData.categories[categoryIndex].fields[rowNumber].answer_possibilities = [
            { opt: '' },
          ];
        } else {
          updatedFormData.categories[categoryIndex].fields[rowNumber].answer_possibilities.push({
            opt: '',
          });
        }

        return updatedFormData;
      });
    }

    function deleteAnswerOpt(categoryIndex, rowNumber, optIndex) {
      setOpenColorIndex(null);
      setFormData((prev) => {
        const updatedFormData = { ...prev };

        updatedFormData.categories[categoryIndex].fields[rowNumber].answer_possibilities.splice(
          optIndex,
          1
        );

        return updatedFormData;
      });
    }

    function handleAnswerOptChange(e, categoryIndex, rowNumber, optIndex) {
      setFormData((prev) => {
        const updatedFormData = { ...prev };
        const target =
          updatedFormData.categories[categoryIndex].fields[rowNumber].answer_possibilities[optIndex];

        if (typeof target === 'string') {
          updatedFormData.categories[categoryIndex].fields[rowNumber].answer_possibilities[
            optIndex
          ] = e.target.value;
        } else {
          target.opt = e.target.value;
        }

        return updatedFormData;
      });
    }

    function handleAnswerOptColorChange(color, categoryIndex, rowNumber, optIndex) {
      setOpenColorIndex(null);
      setFormData((prev) => {
        const updatedFormData = { ...prev };

        if (
          typeof updatedFormData.categories[categoryIndex].fields[rowNumber].answer_possibilities[
            optIndex
          ] === 'string' &&
          color
        ) {
          updatedFormData.categories[categoryIndex].fields[rowNumber].answer_possibilities[
            optIndex
          ] = {
            opt: updatedFormData.categories[categoryIndex].fields[rowNumber]
              .answer_possibilities[optIndex],
            color,
          };
        } else {
          if (color) {
            updatedFormData.categories[categoryIndex].fields[rowNumber].answer_possibilities[
              optIndex
            ].color = color;
          } else {
            delete updatedFormData.categories[categoryIndex].fields[rowNumber].answer_possibilities[
              optIndex
            ].color;
          }
        }

        return updatedFormData;
      });
    }

    return (
      <div
        ref={menuRef}
        role="dialog"
        aria-label="Additional Properties"
        className="fixed mt-[195px] w-[500px] max-h-[calc(100vh-220px)] overflow-y-auto top-0 left-0 right-0 mx-auto bg-slate-100 shadow-lg rounded-lg px-2 z-[20] text-center transition-all text-lg"
      >
        <p className="py-4 text-xl underline text-bold">Additional Properties</p>
        {categoryType === 'numbers' && (
          <div className="mb-2">
            <label
              htmlFor={field.id + '-valuetype'}
              className="block py-2 px-1 rounded-lg bg-white hover:bg-blue-500 hover:text-white w-[100%] mt-2 mb-1"
            >
              Numeric Value Type (export)
            </label>
            <input
              type="checkbox"
              id={field.id + '-valuetype'}
              className="hidden visibility-checkbox"
            />
            <div className="visibility-item transition-all">
              <div className="flex items-center gap-3 py-2">
                <label className="flex items-center gap-2">
                  <input
                    type="radio"
                    name={field.id + '-valuetype-radio'}
                    value="auto"
                    checked={!field.valueType}
                    onChange={() => handleValueTypeSelect('auto', i, j)}
                  />
                  <span className="text-sm">Auto (detect)</span>
                </label>
                <label className="flex items-center gap-2">
                  <input
                    type="radio"
                    name={field.id + '-valuetype-radio'}
                    value="amount"
                    checked={field.valueType === 'amount'}
                    onChange={() => handleValueTypeSelect('amount', i, j)}
                  />
                  <span className="text-sm">Amount</span>
                </label>
                <label className="flex items-center gap-2">
                  <input
                    type="radio"
                    name={field.id + '-valuetype-radio'}
                    value="percent"
                    checked={field.valueType === 'percent'}
                    onChange={() => handleValueTypeSelect('percent', i, j)}
                  />
                  <span className="text-sm">Percent</span>
                </label>
              </div>
              <p className="text-xs text-neutral-600 pb-2 px-1">
                Auto uses topic/formula heuristics. Amount/Percent overrides export formatting for
                this field.
              </p>
            </div>
          </div>
        )}

        <div className="">
          <label
            htmlFor={field.id + '-placeholder'}
            className="block py-2 px-1 rounded-lg bg-white hover:bg-blue-500 hover:text-white w-[100%] mt-2 mb-1"
          >
            Add Placeholder
          </label>
          <input
            type="checkbox"
            id={field.id + '-placeholder'}
            className="hidden visibility-checkbox"
          />
          <div className="visibility-item transition-all">
            <textarea
              value={field.placeholder}
              onChange={(e) => handlePlaceholderChange(e, i, j)}
              className="w-[100%] focus:outline-none border rounded-lg resize-none py-1 px-1"
              placeholder='e.g. enter if "x" if not existent'
            ></textarea>
          </div>
        </div>
        <div className="mb-1">
          <label
            htmlFor={field.id + '-answerpossibilities'}
            className={
              'bg-white block py-2 px-1 rounded-lg hover:bg-blue-500 hover:text-white w-[100%] overflow-visible mt-1 mb-1' +
              (field?.answer_possibilities ? ' text-white bg-blue-500' : '')
            }
          >
            Add Answer Option
          </label>
          <input
            type="checkbox"
            id={field.id + '-answerpossibilities'}
            className="hidden add-opt-btn"
          />
          <div className=" transition-all">
            {field?.answer_possibilities?.map((answer_possibility, l) => {
              const answerLabel = getAnswerOptionValue(answer_possibility) || `Option ${l + 1}`;

              return (
                <div key={l} className="flex items-center justify-between">
                  <input
                    className="w-[87.5%] focus:outline-none p-1 rounded-lg border"
                    value={getAnswerOptionValue(answer_possibility)}
                    onChange={(e) => handleAnswerOptChange(e, i, j, l)}
                  />

                  <AnswerOptionColorPicker
                    answerLabel={answerLabel}
                    selectedColor={answer_possibility?.color}
                    open={openColorIndex === l}
                    placement={colorPlacement}
                    menuRef={openColorIndex === l ? colorMenuRef : undefined}
                    buttonRef={(element) => {
                      colorButtonRefs.current[l] = element;
                    }}
                    onToggle={() => {
                      setOpenColorIndex((currentIndex) => (currentIndex === l ? null : l));
                    }}
                    onSelect={(color) => handleAnswerOptColorChange(color, i, j, l)}
                    onClear={() => handleAnswerOptColorChange(undefined, i, j, l)}
                  />

                  <button
                    type="button"
                    className="mx-auto border-2 p-1.5 border-blue-400 text-blue-400 hover:bg-blue-400 hover:text-white transition-all rounded-full"
                    onClick={() => deleteAnswerOpt(i, j, l)}
                  >
                    <MinusSmIcon className="w-5 h-5" />
                  </button>
                </div>
              );
            })}

            <button
              type="button"
              className="block py-2 px-1 rounded-lg text-center w-[100%] hover:bg-blue-400 hover:text-white transition-all mb-2 mt-1 bg-white"
              onClick={() => addAnswerOpt(i, j)}
            >
              <PlusSmIcon className="w-5 h-5 mx-auto" />
            </button>
          </div>
        </div>
      </div>
    );
  }
);

export default AdditionalPropsMenu;
